import { createContext, FC, ReactNode, useCallback, useContext, useMemo, useState } from 'react';
import { useShowContext } from 'framework/show';
import { EngineeringIncidentReport, ProjectEngineeringGraphqlData } from '../types';
import CreateIncidentModal from './CreateIncidentModal';
import UpdateIncidentModal from './UpdateIncidentModal';

interface IncidentCrudContextValue {
  openCreate: () => void;
  openUpdate: (incident: EngineeringIncidentReport) => void;
}

const IncidentCrudContext = createContext<IncidentCrudContextValue | null>(null);

interface IncidentCrudProviderProps {
  projectId: string;
  children: ReactNode;
}

export const IncidentCrudProvider: FC<IncidentCrudProviderProps> = ({ projectId, children }) => {
  const { data: showData, setData } = useShowContext<ProjectEngineeringGraphqlData>();
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editingIncident, setEditingIncident] = useState<EngineeringIncidentReport | null>(null);

  const value = useMemo<IncidentCrudContextValue>(
    () => ({
      openCreate: () => setIsCreateOpen(true),
      openUpdate: incident => setEditingIncident(incident),
    }),
    [],
  );

  const closeCreate = useCallback(() => setIsCreateOpen(false), []);
  const closeUpdate = useCallback(() => setEditingIncident(null), []);

  const upsertIncident = useCallback(
    (incident: EngineeringIncidentReport) => {
      const current = showData.getData();
      const incidents = current?.engineeringIncidents ?? [];
      const nextIncidents = incidents.some(item => item.id === incident.id)
        ? incidents.map(item => (item.id === incident.id ? incident : item))
        : [incident, ...incidents];
      setData?.({ ...current, engineeringIncidents: nextIncidents });
    },
    [showData, setData],
  );

  return (
    <IncidentCrudContext.Provider value={value}>
      {children}
      <CreateIncidentModal projectId={projectId} isOpen={isCreateOpen} onClose={closeCreate} onSaved={upsertIncident} />
      <UpdateIncidentModal
        projectId={projectId}
        incident={editingIncident}
        isOpen={!!editingIncident}
        onClose={closeUpdate}
        onSaved={upsertIncident}
      />
    </IncidentCrudContext.Provider>
  );
};

export const useIncidentCrud = (): IncidentCrudContextValue => {
  const ctx = useContext(IncidentCrudContext);
  if (!ctx) {
    throw new Error('useIncidentCrud must be used within an IncidentCrudProvider');
  }
  return ctx;
};
